import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import type { IndicatorInput, SessionTimingPolicy } from '@einhaus/project314-types';
import type { StatusTone } from '@/components/UI/StatusBadge';
import type { AppRouter } from '@/types/appRouter';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type RouterInputs = inferRouterInputs<AppRouter>;

export type IdeaLabStudySummary = RouterOutputs['ideaLab']['listStudies'][number];
export type IdeaLabStudyDetail = RouterOutputs['ideaLab']['getStudy'];
export type IdeaLabVariantSummary = IdeaLabStudyDetail['variants'][number];
export type IdeaLabVariantDetail = RouterOutputs['ideaLab']['getVariant'];
export type IdeaLabFreezeResult = RouterOutputs['ideaLab']['freezeVariant'];
export type IdeaLabRunSummary = RouterOutputs['ideaLab']['listRuns'][number];
export type IdeaLabRunDetail = RouterOutputs['ideaLab']['getRun'];
export type IdeaLabRunReport = RouterOutputs['ideaLab']['getRunReport'];
export type IdeaLabRunResults = RouterOutputs['ideaLab']['getRunResults'];
export type IdeaLabRunPreview = RouterOutputs['ideaLab']['previewRun'];
export type IdeaLabRunComparison = RouterOutputs['ideaLab']['compareRuns'];
export type IdeaLabEntryReviewPage = RouterOutputs['ideaLab']['listEntryReviewCases'];
export type IdeaLabEntryReviewCase = IdeaLabEntryReviewPage['cases'][number];
export type IdeaLabEntryReviewPreview = RouterOutputs['ideaLab']['previewEntryReviewWindow'];
export type IdeaLabRunCell = IdeaLabRunReport['cells'][number];
export type IdeaLabRunEntityCell = IdeaLabRunReport['entityCells'][number];
export type IdeaLabRunDiffCell = IdeaLabRunReport['diffs'][number];
export type IdeaLabRunOptionReason = IdeaLabRunReport['optionReasons'][number];
export type IdeaLabRunVariant = IdeaLabRunReport['variants'][number];
export type CreateIdeaLabRunInput = RouterInputs['ideaLab']['createRun'];
export type IdeaLabPromotionReceipt = RouterOutputs['ideaLab']['getPromotion'];
export type PromoteIdeaLabVariantInput = RouterInputs['ideaLab']['promoteVariant'];
export type IdeaLabPromotionStatus = IdeaLabPromotionReceipt['status'];
export type CreateIdeaLabVariantInput = RouterInputs['ideaLab']['createVariant'];
export type IdeaLabRunStatus = IdeaLabRunSummary['status'];
export type IdeaLabVariantLifecycle = IdeaLabVariantSummary['lifecycle'];
export type IdeaLabWindowRole = IdeaLabRunCell['windowRole'];
export type IdeaLabLens = IdeaLabRunCell['lens'];
export type IdeaLabRunOptionLens = NonNullable<IdeaLabRunSummary['optionLens']>;
export type IdeaLabRunExposure = IdeaLabRunReport['exposure'];
export type IdeaLabPromotionExposure = IdeaLabPromotionReceipt['exposure'];
export type IdeaLabGridAxis = RouterInputs['ideaLab']['createVariantGrid']['axes'][number];
export type IdeaLabGridPreview = RouterOutputs['ideaLab']['previewVariantGrid'];
export type IdeaLabGridResult = RouterOutputs['ideaLab']['createVariantGrid'];
/** A rule setting a grid can vary, as the backend lists it, with the axis template that varies it. */
export type IdeaLabGridTarget = RouterOutputs['ideaLab']['listVariantGridTargets'][number];
export type CreateIdeaLabTimingHypothesisInput = RouterInputs['ideaLab']['createEntryTimingHypothesis'];
type GridScalar = number | string | boolean;
export type IdeaLabGridValue = GridScalar | GridScalar[];

export type IdeaLabChartIndicator = Extract<IndicatorInput, { type: 'ema' } | { type: 'sma' }>;

const MOVING_AVERAGE_SELECTOR_KEYS = new Set(['level', 'primaryLevel', 'secondaryLevel']);
const MOVING_AVERAGE_SELECTOR_PATTERN = /^(ema|sma)-([1-9]\d*)$/;

export const describeSessionTimingPolicy = (duration: string, policy: SessionTimingPolicy | undefined): string => {
    if (!policy) return 'Unavailable';
    if (policy.executionPolicy === 'nearClose') return `${policy.preCloseOffsetSecs ?? 0} seconds before session close`;
    if (policy.executionPolicy === 'nextOpen') {
        return duration.endsWith('m') && !duration.endsWith('mo')
            ? 'Next bar open; after the final session bar, next trading-session open'
            : 'Next trading-session open';
    }
    return 'Completed bar close + 1 second';
};

/** Derive the moving-average overlays referenced by a frozen trade-logic payload. */
export const deriveIdeaLabChartIndicators = (payload: unknown): IdeaLabChartIndicator[] => {
    const indicators = new Map<string, IdeaLabChartIndicator>();

    const visit = (value: unknown): void => {
        if (Array.isArray(value)) {
            value.forEach(visit);
            return;
        }
        if (typeof value !== 'object' || value === null) return;

        for (const [key, nestedValue] of Object.entries(value)) {
            if (MOVING_AVERAGE_SELECTOR_KEYS.has(key) && typeof nestedValue === 'string') {
                const match = MOVING_AVERAGE_SELECTOR_PATTERN.exec(nestedValue);
                const type = match?.[1];
                const period = Number(match?.[2]);

                if ((type === 'ema' || type === 'sma') && Number.isSafeInteger(period) && period > 0) {
                    indicators.set(`${type}-${period}`, { type, period });
                }
            }

            visit(nestedValue);
        }
    };

    visit(payload);

    return [...indicators.values()].sort((left, right) => left.period - right.period || left.type.localeCompare(right.type));
};

export const formatIdeaLabTraceMarkerLabel = (event: { kind: string; stepId?: string | null }): string => {
    const stepMarker = '::step:';
    const stepMarkerIndex = event.stepId?.lastIndexOf(stepMarker) ?? -1;
    const label = stepMarkerIndex >= 0 ? event.stepId?.slice(stepMarkerIndex + stepMarker.length) : event.kind;

    return (label ?? event.kind)
        .replaceAll('-', ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .toLowerCase();
};

export const LENS_LABELS: Record<IdeaLabLens, string> = {
    underlying: 'Underlying',
    option: 'Options',
    futures: 'ETF signals → futures'
};

export const LENS_ORDER: readonly IdeaLabLens[] = ['underlying', 'option', 'futures'];

/** Lenses a run measured, in display order. */
export const runLenses = (run: { includeUnderlying: boolean; optionLens: unknown | null; futures?: readonly unknown[] }): IdeaLabLens[] => [
    ...(run.includeUnderlying ? (['underlying'] as const) : []),
    ...(run.optionLens !== null ? (['option'] as const) : []),
    ...(run.futures?.length ? (['futures'] as const) : [])
];

export const describeRunLenses = (run: {
    includeUnderlying: boolean;
    optionLens: { optionBuyingStrategyId: number } | null;
    futures?: readonly unknown[];
}): string =>
    runLenses(run)
        .map((lens) =>
            lens === 'option' ? `${LENS_LABELS.option} (strategy #${run.optionLens?.optionBuyingStrategyId})` : LENS_LABELS[lens]
        )
        .join(' · ');

export const IDEA_LAB_ACTIVE_RUN_STATUSES: readonly IdeaLabRunStatus[] = ['queued', 'running', 'reducing'];

export const isIdeaLabRunActive = (run: { status: IdeaLabRunStatus }): boolean => IDEA_LAB_ACTIVE_RUN_STATUSES.includes(run.status);

export const RUN_STATUS_LABELS: Record<IdeaLabRunStatus, string> = {
    queued: 'Queued',
    running: 'Evaluating',
    reducing: 'Building report',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled'
};

export const getRunStatusTone = (status: IdeaLabRunStatus): StatusTone => {
    switch (status) {
        case 'queued':
            return 'accent';
        case 'running':
        case 'reducing':
            return 'info';
        case 'completed':
            return 'success';
        case 'failed':
            return 'danger';
        case 'cancelled':
            return 'warning';
    }
};

export const VARIANT_LIFECYCLE_LABELS: Record<IdeaLabVariantLifecycle, string> = {
    draft: 'Draft',
    frozen: 'Frozen',
    archived: 'Archived'
};

export const getVariantLifecycleTone = (lifecycle: IdeaLabVariantLifecycle): StatusTone => {
    switch (lifecycle) {
        case 'draft':
            return 'neutral';
        case 'frozen':
            return 'success';
        case 'archived':
            return 'warning';
    }
};

export const WINDOW_ROLE_LABELS: Record<IdeaLabWindowRole, string> = {
    training: 'Training',
    walkForward: 'Walk-forward',
    wholeRange: 'Whole range'
};

export const WINDOW_ROLE_ORDER: readonly IdeaLabWindowRole[] = ['training', 'walkForward', 'wholeRange'];

/** Only training evidence may originate a timing hypothesis; later windows remain validation-only. */
export const canCreateEntryTimingHypothesis = (windowRole: IdeaLabWindowRole): boolean => windowRole === 'training';

/** Builds the exact mutation coordinate from the selected evidence cell. */
export const buildEntryTimingHypothesisInput = (params: {
    entityType?: 'Asset' | 'AssetIndex' | 'Cryptocurrency' | undefined;
    runId: number;
    variantId: number;
    lens: IdeaLabLens;
    horizonSeconds: number;
    cell: Pick<CreateIdeaLabTimingHypothesisInput, 'weekday' | 'intradayBucketKey'>;
}): CreateIdeaLabTimingHypothesisInput => ({
    ...(params.entityType === undefined ? {} : { entityType: params.entityType }),
    horizonSeconds: params.horizonSeconds,
    intradayBucketKey: params.cell.intradayBucketKey,
    lens: params.lens,
    runId: params.runId,
    variantId: params.variantId,
    weekday: params.cell.weekday
});

export const RANDOM_EDGE_BASIS_LABELS: Record<IdeaLabRunCell['randomEdgeBasis'], string> = {
    unavailable: 'no reference',
    contextual: 'contextual reference',
    exact: 'exact control'
};

export const parsePositiveInt = (value: string | null | undefined): number | null => {
    if (!value) return null;

    const parsed = Number.parseInt(value, 10);

    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

/** Percent values arrive already in percent units (0.25 means 0.25%). */
export const formatSignedPercent = (value: number | null, digits = 3): string => {
    if (value === null || !Number.isFinite(value)) return '—';

    const sign = value > 0 ? '+' : '';

    return `${sign}${value.toFixed(digits)}%`;
};

export const formatPlainPercent = (value: number | null, digits = 1): string =>
    value === null || !Number.isFinite(value) ? '—' : `${value.toFixed(digits)}%`;

export const formatShare = (value: number | null): string => (value === null ? '—' : `${(value * 100).toFixed(0)}%`);

const dollarFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

/** Signed dollar amounts for the option lens economics (one contract). */
export const formatSignedDollars = (value: number | null): string => {
    if (value === null || !Number.isFinite(value)) return '—';

    const formatted = dollarFormatter.format(Math.abs(value));

    return value < 0 ? `-${formatted}` : value > 0 ? `+${formatted}` : formatted;
};

export const formatDollars = (value: number | null): string =>
    value === null || !Number.isFinite(value) ? '—' : dollarFormatter.format(value);

export const percentToneClass = (value: number | null): string => {
    if (value === null || value === 0) return 'text-zinc-300';

    return value > 0 ? 'text-green-400' : 'text-red-400';
};

const marketDateTimeFormatter = new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' });

export const formatEasternUnix = (unix: number | null): string => (unix === null ? '—' : marketDateTimeFormatter.format(unix * 1_000));

/** Run windows are stored as Eastern wall-clock strings and are displayed verbatim. */
export const formatWallClock = (value: string | null): string => value ?? '—';

export const formatHorizonLabel = (seconds: number): string => {
    if (seconds % 86_400 === 0) {
        const days = seconds / 86_400;

        return days === 1 ? '1 day' : `${days} days`;
    }

    if (seconds % 3_600 === 0) {
        const hours = seconds / 3_600;

        return hours === 1 ? '1 hour' : `${hours} hours`;
    }

    if (seconds % 60 === 0) return `${seconds / 60} min`;

    return `${seconds}s`;
};

export const describeRunWindow = (run: { startDateTime: string; endDateTime: string; walkForwardStartDateTime: string | null }): string =>
    run.walkForwardStartDateTime
        ? `${run.startDateTime} → ${run.walkForwardStartDateTime} (training) → ${run.endDateTime}`
        : `${run.startDateTime} → ${run.endDateTime}`;

export interface RunProgress {
    total: number;
    completed: number;
    failed: number;
    excluded: number;
    percent: number;
}

export const computeRunProgress = (run: {
    workUnitCount: number;
    completedWorkUnitCount: number;
    failedWorkUnitCount: number;
    excludedWorkUnitCount: number;
}): RunProgress => {
    const total = run.workUnitCount;
    const done = run.completedWorkUnitCount + run.failedWorkUnitCount + run.excludedWorkUnitCount;

    return {
        total,
        completed: run.completedWorkUnitCount,
        failed: run.failedWorkUnitCount,
        excluded: run.excludedWorkUnitCount,
        percent: total === 0 ? 0 : Math.min(100, Math.round((done / total) * 100))
    };
};

export type ReportMetric =
    | 'meanReturnPercent'
    | 'medianReturnPercent'
    | 'benchmarkEdgeMeanPercent'
    | 'randomEdgeMeanPercent'
    | 'winRate'
    | 'supportCount'
    | 'futuresMeanNetCash'
    | 'futuresMeanReturnOnAllocatedCapitalPercent'
    | 'optionMeanNetProfit'
    | 'tradabilityRate';

/** Metrics only meaningful on one lens; every other metric applies to both. */
export const METRIC_LENSES: Partial<Record<ReportMetric, IdeaLabLens>> = {
    benchmarkEdgeMeanPercent: 'underlying',
    optionMeanNetProfit: 'option',
    futuresMeanNetCash: 'futures',
    futuresMeanReturnOnAllocatedCapitalPercent: 'futures',
    tradabilityRate: 'option'
};

export const metricOptionsForLens = (lens: IdeaLabLens): { value: ReportMetric; label: string; description: string }[] =>
    REPORT_METRIC_OPTIONS.filter(
        (option) => (METRIC_LENSES[option.value] ?? lens) === lens && !(lens === 'futures' && option.value === 'randomEdgeMeanPercent')
    );

export const REPORT_METRIC_OPTIONS: { value: ReportMetric; label: string; description: string }[] = [
    {
        value: 'futuresMeanNetCash',
        label: 'Mean native net cash (USD)',
        description: 'Dated futures price-change cash after both fees; reserve is not a payment.'
    },
    {
        value: 'futuresMeanReturnOnAllocatedCapitalPercent',
        label: 'Mean return on allocated capital',
        description: 'Native net cash divided by the actual opening reserve, separately from price return.'
    },
    {
        value: 'meanReturnPercent',
        label: 'Mean return',
        description: 'Mean fixed-horizon return of priced entries (options: net dollars over the one-contract opening premium)'
    },
    { value: 'medianReturnPercent', label: 'Median return', description: 'Median fixed-horizon return of priced entries' },
    { value: 'benchmarkEdgeMeanPercent', label: 'Edge vs SPY', description: 'Mean of return minus the same-window SPY return' },
    {
        value: 'randomEdgeMeanPercent',
        label: 'Edge vs random',
        description:
            'Mean of return minus the platform random-entry reference for the entity month. The Reference column shows whether that contextual reference is covered.'
    },
    { value: 'winRate', label: 'Win rate', description: 'Share of priced entries with a positive return' },
    { value: 'supportCount', label: 'Support', description: 'Priced entries in the cell' },
    {
        value: 'optionMeanNetProfit',
        label: 'Mean net P&L ($)',
        description: 'Mean net dollar P&L of one contract after fees, over priced entries'
    },
    {
        value: 'tradabilityRate',
        label: 'Tradability',
        description: 'Share of entries where a contract matched the policy and could be entered (priced or still immature)'
    }
];

/** Entries of a cell across every outcome state: priced, immature, and missing. */
export const cellEntryCount = (cell: Pick<IdeaLabRunCell, 'supportCount' | 'immatureCount' | 'missingCount'>): number =>
    cell.supportCount + cell.immatureCount + cell.missingCount;

/** Share of a cell's entries whose contract was selected and entered (the plan's tradability rate). */
export const tradabilityRate = (
    cell: Pick<IdeaLabRunCell, 'supportCount' | 'immatureCount' | 'missingCount' | 'option'>
): number | null => {
    const total = cellEntryCount(cell);

    return total === 0 ? null : ((total - cell.option.noContractCount - cell.option.notEnteredCount) / total) * 100;
};

export const metricValue = (cell: IdeaLabRunCell, metric: ReportMetric): number | null => {
    switch (metric) {
        case 'meanReturnPercent':
            return cell.meanReturnPercent;
        case 'medianReturnPercent':
            return cell.medianReturnPercent;
        case 'benchmarkEdgeMeanPercent':
            return cell.benchmarkEdgeMeanPercent;
        case 'randomEdgeMeanPercent':
            return cell.randomEdgeMeanPercent;
        case 'winRate':
            return cell.supportCount === 0 ? null : (cell.winCount / cell.supportCount) * 100;
        case 'supportCount':
            return cell.supportCount;
        case 'futuresMeanNetCash':
            return cell.futures.meanNetCash;
        case 'futuresMeanReturnOnAllocatedCapitalPercent':
            return cell.futures.meanReturnOnAllocatedCapitalPercent;
        case 'optionMeanNetProfit':
            return cell.option.meanNetProfit;
        case 'tradabilityRate':
            return tradabilityRate(cell);
    }
};

export const formatMetricValue = (metric: ReportMetric, value: number | null): string => {
    switch (metric) {
        case 'supportCount':
            return value === null ? '—' : String(Math.round(value));
        case 'winRate':
        case 'tradabilityRate':
            return formatPlainPercent(value, 1);
        case 'futuresMeanNetCash':
        case 'optionMeanNetProfit':
            return formatSignedDollars(value);
        default:
            return formatSignedPercent(value, 3);
    }
};

export const metricCaption = (cell: IdeaLabRunCell, metric: ReportMetric): string => {
    if (metric === 'randomEdgeMeanPercent') {
        return `${cell.randomEdgeCoveredCount}/${cell.supportCount} covered · ${RANDOM_EDGE_BASIS_LABELS[cell.randomEdgeBasis]}`;
    }

    if (metric === 'supportCount') {
        return `${cell.immatureCount} immature · ${cell.missingCount} missing`;
    }

    if (metric === 'tradabilityRate') {
        return `${cell.option.noContractCount} no contract · ${cell.option.notEnteredCount} not entered · ${cellEntryCount(cell)} entries`;
    }

    if (metric === 'futuresMeanNetCash' || metric === 'futuresMeanReturnOnAllocatedCapitalPercent')
        return `n=${cell.supportCount} · reserve ${formatDollars(cell.futures.meanOpeningReserve)} · fees ${formatDollars(cell.futures.meanFees)}`;
    if (metric === 'optionMeanNetProfit') {
        return `n=${cell.supportCount} · premium ${formatDollars(cell.option.meanOpeningPremium)} · fees ${formatDollars(cell.option.meanFees)}`;
    }

    return `n=${cell.supportCount} · ${cell.distinctEntityCount} entities · ${cell.distinctDateCount} dates`;
};

export interface ReportMatrixCell {
    cell: IdeaLabRunCell;
    value: number | null;
    display: string;
    caption: string;
}

export interface ReportMatrixRow {
    variant: IdeaLabRunVariant;
    cells: Map<number, ReportMatrixCell>;
}

export interface ReportMatrix {
    horizons: number[];
    rows: ReportMatrixRow[];
}

/** Variant rows by fixed horizon for one lens, window, and metric; missing cells stay absent. */
export const buildReportMatrix = (
    report: IdeaLabRunReport,
    lens: IdeaLabLens,
    windowRole: IdeaLabWindowRole,
    metric: ReportMetric
): ReportMatrix => {
    const horizons = [...new Set(report.horizons.filter((horizon) => horizon.lens === lens).map((horizon) => horizon.horizonSeconds))].sort(
        (left, right) => left - right
    );
    const rows: ReportMatrixRow[] = report.variants.map((variant) => ({ variant, cells: new Map() }));
    const rowByVariantId = new Map(rows.map((row) => [row.variant.variantId, row] as const));

    for (const cell of report.cells) {
        if (cell.lens !== lens || cell.windowRole !== windowRole) continue;

        const row = rowByVariantId.get(cell.variantId);

        if (!row) continue;

        const value = metricValue(cell, metric);

        row.cells.set(cell.horizonSeconds, {
            cell,
            value,
            display: formatMetricValue(metric, value),
            caption: metricCaption(cell, metric)
        });
    }

    return { horizons, rows };
};

export const availableWindowRoles = (report: Pick<IdeaLabRunReport, 'cells'>): IdeaLabWindowRole[] => {
    const present = new Set(report.cells.map((cell) => cell.windowRole));

    return WINDOW_ROLE_ORDER.filter((role) => present.has(role));
};

/** Plain-language labels of the worker's stable option reason codes; unknown codes show verbatim. */
const OPTION_REASON_LABELS: Record<string, string> = {
    NO_CONTRACT: 'No contract matched the policy',
    INVALID_ENTRY_PRICE: 'The entry had no executable underlying price',
    MISSING_HORIZON_CALENDAR: 'The horizon could not be placed on the trading calendar',
    HORIZON_NOT_MATURE: 'The horizon ends after the knowledge cutoff',
    HORIZON_EXECUTION_NOT_OBSERVABLE_AT_EVIDENCE_CUTOFF: 'The closing quote lies after the knowledge cutoff',
    SETTLEMENT_NOT_OBSERVABLE_AT_EVIDENCE_CUTOFF: 'Settlement lies after the knowledge cutoff',
    INCOMPLETE_LIFECYCLE: 'The position had not closed by the knowledge cutoff',
    MISSING_VERIFIED_CONTRACT: 'The selected contract has no verified catalog terms',
    UNVERIFIED_OR_UNSUPPORTED_CONTRACT: 'The selected contract is not a verified standard contract',
    CONTRACT_ENTITY_MISMATCH: 'The selected contract belongs to another underlying',
    MISSING_ENTRY_PRICE_EPOCH: 'No underlying minute bar existed at the entry',
    ENTRY_QUOTE_SOURCE_UNAVAILABLE: 'No option quote existed at the selected entry quote time',
    ENTRY_QUOTE_SOURCE_MISMATCH: 'The entry quote differs from the quote source',
    ENTRY_QUOTE_UNUSABLE: 'The entry quote was not usable',
    INVALID_ENTRY_QUOTE: 'The entry quote was not usable',
    INVALID_ENTRY_QUOTE_SOURCE: 'The entry quote source was not usable',
    ENTRY_OUTSIDE_EXECUTABLE_WINDOW: 'The position could not open before the horizon end or the last trade'
};

const EVALUATION_FAILURE_PREFIX = 'EVALUATION_FAILURE:';

export const describeOptionReason = (reasonCode: string): string => {
    if (reasonCode.startsWith(EVALUATION_FAILURE_PREFIX)) {
        return `Replay failed: ${reasonCode.slice(EVALUATION_FAILURE_PREFIX.length).toLowerCase().replaceAll('_', ' ')}`;
    }

    return OPTION_REASON_LABELS[reasonCode] ?? (reasonCode === '' ? 'No reason recorded' : reasonCode);
};

export interface OptionReasonRow {
    reasonCode: string;
    outcomeState: IdeaLabRunOptionReason['outcomeState'];
    label: string;
    /** Entry count per horizon; horizons without this reason are absent. */
    counts: Map<number, number>;
    total: number;
}

/** One variant's unpriced option entries pivoted to reason rows by horizon columns (pure). */
export const buildOptionReasonRows = (report: Pick<IdeaLabRunReport, 'optionReasons'>, variantId: number | null): OptionReasonRow[] => {
    const rows = new Map<string, OptionReasonRow>();

    for (const reason of report.optionReasons) {
        if (reason.variantId !== variantId) continue;

        const key = `${reason.outcomeState}:${reason.reasonCode}`;
        const row = rows.get(key) ?? {
            reasonCode: reason.reasonCode,
            outcomeState: reason.outcomeState,
            label: describeOptionReason(reason.reasonCode),
            counts: new Map<number, number>(),
            total: 0
        };

        row.counts.set(reason.horizonSeconds, (row.counts.get(reason.horizonSeconds) ?? 0) + reason.entryCount);
        row.total += reason.entryCount;
        rows.set(key, row);
    }

    return [...rows.values()].sort((left, right) => right.total - left.total || left.reasonCode.localeCompare(right.reasonCode));
};

/** Lenses the report has horizons for, in display order. */
export const availableLenses = (report: Pick<IdeaLabRunReport, 'horizons'>): IdeaLabLens[] => {
    const present = new Set(report.horizons.map((horizon) => horizon.lens));

    return LENS_ORDER.filter((lens) => present.has(lens));
};

/** Frozen entry variants that can be evaluated together: they must share one duration. */
export const runnableVariants = (variants: IdeaLabVariantSummary[]): IdeaLabVariantSummary[] =>
    variants.filter((variant) => variant.lifecycle === 'frozen' && variant.kind === 'entry');

export const slugifyVariantKey = (label: string): string =>
    label
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9._-]+/g, '-')
        .replace(/^[^a-z0-9]+/, '')
        .replace(/-+$/, '')
        .slice(0, 64);

export const PROMOTION_STATUS_LABELS: Record<IdeaLabPromotionStatus, string> = {
    verification_pending: 'Created · verification pending',
    verification_passed: 'Created · entries verified',
    verification_failed: 'Created · verification failed'
};

export const getPromotionStatusTone = (status: IdeaLabPromotionStatus): StatusTone => {
    switch (status) {
        case 'verification_pending':
            return 'warning';
        case 'verification_passed':
            return 'success';
        case 'verification_failed':
            return 'danger';
    }
};

/**
 * One key per open promotion dialog. Re-clicking Promote inside the same dialog reuses it, so the
 * server returns the same receipt instead of creating a second strategy.
 */
export const buildPromotionIdempotencyKey = (runId: number, variantId: number, nonce: string = crypto.randomUUID()): string =>
    `bench:run:${runId}:variant:${variantId}:${nonce}`;

/** Editor route that opens a draft variant's rules and saves them back to the variant. */
export const buildIdeaLabRulesEditorPath = (variantId: number): string => {
    const params = new URLSearchParams({ ideaLabVariantId: String(variantId), returnTo: `/user/ideaLab/variant/${variantId}` });

    return `/user/tradeLogic/entry/new?${params.toString()}`;
};

/** The next free `<sourceKey>-nudge-N` key within a study. */
export const nextNudgeVariantKey = (existingKeys: readonly string[], sourceKey: string): string => {
    const taken = new Set(existingKeys);
    let n = 1;

    while (taken.has(`${sourceKey}-nudge-${n}`)) n += 1;

    return `${sourceKey}-nudge-${n}`;
};

const isGridScalar = (value: unknown): value is GridScalar =>
    typeof value === 'string' || typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value));

/** A target's current value as a grid value; the backend only lists scalar or scalar-list settings. */
export const toGridValue = (value: unknown): IdeaLabGridValue => {
    if (isGridScalar(value)) return value;
    if (Array.isArray(value) && value.length > 0 && value.every(isGridScalar)) return value;

    throw new Error(`Grid target value ${JSON.stringify(value)} is not a scalar or a list of scalars`);
};

const parseGridScalar = (text: string, like: GridScalar): GridScalar | null => {
    if (typeof like === 'number') {
        const value = Number(text);
        return text !== '' && Number.isFinite(value) ? value : null;
    }

    if (typeof like === 'boolean') {
        return text === 'true' ? true : text === 'false' ? false : null;
    }

    return text === '' ? null : text;
};

/**
 * Values typed for a grid target: comma-separated for a single value ("25, 30, 35"), and for a
 * range or list value one entry per ";" with its elements separated by commas ("1, 5; 2, 6").
 */
export const parseGridValues = (text: string, current: IdeaLabGridValue): { values: IdeaLabGridValue[] } | { error: string } => {
    const entries = Array.isArray(current) ? text.split(';') : text.split(',');
    const values: IdeaLabGridValue[] = [];

    for (const rawEntry of entries.map((entry) => entry.trim()).filter((entry) => entry !== '')) {
        if (!Array.isArray(current)) {
            const value = parseGridScalar(rawEntry, current);

            if (value === null) return { error: `"${rawEntry}" is not a ${typeof current}` };
            values.push(value);
            continue;
        }

        const elements = rawEntry.split(',').map((element) => element.trim());

        if (elements.length !== current.length) {
            return { error: `"${rawEntry}" needs ${current.length} values like the current one` };
        }

        const parsed = elements.map((element, index) => parseGridScalar(element, current[index]!));

        if (parsed.some((value) => value === null)) return { error: `"${rawEntry}" does not match the current value's types` };
        values.push(parsed as GridScalar[]);
    }

    return { values };
};

export const formatGridValue = (value: IdeaLabGridValue): string => (Array.isArray(value) ? value.join(', ') : String(value));

/** A neighbouring distance for an example: half of an integer, or the leading decimal place of a fraction. */
const gridExampleStep = (value: number): number =>
    Number.isInteger(value) ? Math.max(1, Math.round(Math.abs(value) / 2)) : 10 ** Math.floor(Math.log10(Math.abs(value)));

const roundGridExample = (value: number, step: number): number => Number(value.toFixed(Math.max(0, 2 - Math.floor(Math.log10(step)))));

const shiftGridExample = (value: GridScalar): GridScalar => {
    if (typeof value !== 'number') return value;

    const step = gridExampleStep(value);

    return roundGridExample(value + step, step);
};

/** Placeholder showing values near the current one in the input's own syntax, e.g. "e.g. 0.05, 0.15, 0.25". */
export const suggestGridValuesExample = (current: IdeaLabGridValue): string => {
    if (Array.isArray(current)) return `e.g. ${current.join(', ')}; ${current.map(shiftGridExample).join(', ')}`;
    if (typeof current === 'boolean') return 'e.g. true, false';
    if (typeof current === 'string') return `e.g. ${current}, another value`;

    const step = gridExampleStep(current);

    return `e.g. ${[current - step, current, current + step].map((value) => roundGridExample(value, step)).join(', ')}`;
};

/** The grid axis that tries `values` for one target. */
export const buildGridAxis = (target: Pick<IdeaLabGridTarget, 'axis'>, values: IdeaLabGridValue[]): IdeaLabGridAxis => ({
    ...target.axis,
    values
});

/** One line under a target's input: its current value, where it is used, and its feature. */
export const describeGridTarget = (target: Pick<IdeaLabGridTarget, 'current' | 'occurrences' | 'featureKey'>): string =>
    [
        `Now ${formatGridValue(toGridValue(target.current))}`,
        target.occurrences > 1 ? `used in ${target.occurrences} places, changed together` : null,
        target.featureKey
    ]
        .filter((part): part is string => part !== null)
        .join(' · ');

/** One rule setting that differs between a variant and its parent. */
export interface VariantRuleChange {
    /** Where the setting lives, named by rule step, feature, and operator, e.g. "established-extension › priceAction.level.distance.percent (ema-8) >=". */
    path: string;
    /** The parent's value; null when only the variant has it. */
    before: string | null;
    /** The variant's value; null when the variant removed it. */
    after: string | null;
}

/** Wording, not logic: renaming or re-describing a copy is not a rule change. */
const RULE_DIFF_IGNORED_KEYS = new Set(['label', 'description', 'tags']);
/** Structural keys that add nothing to a readable path. */
const RULE_DIFF_SILENT_KEYS = new Set([
    'children',
    'child',
    'steps',
    'when',
    'right',
    'left',
    'ref',
    'value',
    'metadata',
    'parameters',
    'importedConditionExpression',
    'featureRef'
]);

type JsonRecord = Record<string, unknown>;

const isJsonRecord = (value: unknown): value is JsonRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

/** Display name for a feature key, e.g. from the feature registry; the key itself when unknown. */
type FeatureLabel = (featureKey: string) => string;

const describeOperand = (operand: unknown, featureLabel: FeatureLabel): string | null => {
    if (!isJsonRecord(operand)) return null;

    const ref = isJsonRecord(operand.ref) ? operand.ref : operand;

    if (typeof ref.featureKey !== 'string') return null;

    const selectors = isJsonRecord(ref.selectors) ? Object.values(ref.selectors).filter((value) => typeof value !== 'object') : [];

    const name = featureLabel(ref.featureKey);

    return selectors.length > 0 ? `${name} (${selectors.join(', ')})` : name;
};

/** A readable name for a rule object, or null when it is only structure. */
const describeRuleObject = (value: JsonRecord, featureLabel: FeatureLabel): string | null => {
    if (value.kind === 'comparison') {
        const operand = describeOperand(value.left, featureLabel);

        return operand === null ? null : `${operand} ${typeof value.operator === 'string' ? value.operator : ''}`.trim();
    }

    if (value.kind === 'condition' && isJsonRecord(value.featureRef)) {
        return typeof value.label === 'string' && value.label.trim() !== '' ? value.label : describeOperand(value.featureRef, featureLabel);
    }

    if (typeof value.id === 'string' && 'when' in value) return value.id;

    return null;
};

const summarizeRuleValue = (value: unknown, featureLabel: FeatureLabel): string => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value) && value.every((item) => typeof item !== 'object' || item === null)) return value.join(', ');
    if (isJsonRecord(value)) {
        return (
            describeRuleObject(value, featureLabel) ??
            (typeof value.label === 'string' ? value.label : typeof value.id === 'string' ? value.id : 'rule part')
        );
    }
    if (Array.isArray(value)) return `${value.length} rule parts`;

    return String(value);
};

const idOf = (value: unknown): string | null => (isJsonRecord(value) && typeof value.id === 'string' ? value.id : null);

/**
 * Every rule setting a variant changed relative to its parent, found by walking both rule trees:
 * list items with ids (nodes, sequence steps) are matched by id, everything else by position, and
 * wording (labels, descriptions, tags) is ignored. Works for structured conditions and imported rule
 * expressions alike; a setting stored in two places is reported once. Features are named with
 * `featureLabel` when given. Pure.
 */
export const diffVariantRules = (
    child: unknown,
    parent: unknown,
    featureLabel: FeatureLabel = (featureKey) => featureKey
): VariantRuleChange[] => {
    const changes = new Map<string, VariantRuleChange>();
    const record = (path: string[], before: string | null, after: string | null) => {
        const change = { path: path.length > 0 ? path.join(' › ') : 'Rules', before, after };

        changes.set(`${change.path}|${before}|${after}`, change);
    };

    const walk = (after: unknown, before: unknown, path: string[], key: string | null): void => {
        const leafPath = key !== null && !RULE_DIFF_SILENT_KEYS.has(key) && !/^\d+$/.test(key) ? [...path, key] : path;

        if (after === undefined || before === undefined) {
            if (after !== before)
                record(
                    leafPath,
                    before === undefined ? null : summarizeRuleValue(before, featureLabel),
                    after === undefined ? null : summarizeRuleValue(after, featureLabel)
                );
            return;
        }

        if (Array.isArray(after) && Array.isArray(before)) {
            const matchById = [...after, ...before].length > 0 && [...after, ...before].every((item) => idOf(item) !== null);

            if (matchById) {
                const beforeById = new Map(before.map((item) => [idOf(item)!, item]));

                for (const item of after) {
                    walk(item, beforeById.get(idOf(item)!), path, null);
                    beforeById.delete(idOf(item)!);
                }

                for (const removed of beforeById.values()) walk(undefined, removed, path, null);
                return;
            }

            if ([...after, ...before].every((item) => typeof item !== 'object' || item === null)) {
                if (after.join('\u0000') !== before.join('\u0000'))
                    record(leafPath, summarizeRuleValue(before, featureLabel), summarizeRuleValue(after, featureLabel));
                return;
            }

            for (let index = 0; index < Math.max(after.length, before.length); index += 1)
                walk(after[index], before[index], path, String(index));
            return;
        }

        if (isJsonRecord(after) && isJsonRecord(before)) {
            const name = describeRuleObject(after, featureLabel) ?? describeRuleObject(before, featureLabel);
            const objectPath = name === null ? leafPath : [...path, name];

            for (const nestedKey of new Set([...Object.keys(after), ...Object.keys(before)])) {
                if (!RULE_DIFF_IGNORED_KEYS.has(nestedKey)) walk(after[nestedKey], before[nestedKey], objectPath, nestedKey);
            }
            return;
        }

        if (JSON.stringify(after) !== JSON.stringify(before))
            record(leafPath, summarizeRuleValue(before, featureLabel), summarizeRuleValue(after, featureLabel));
    };

    walk(child, parent, [], null);

    return [...changes.values()];
};

const countOf = (count: number, singular: string, plural = `${singular}s`): string => `${count} ${count === 1 ? singular : plural}`;

/**
 * Plain-language reading of a run's exposure. Idea Lab reports show every window as soon as a run
 * completes, so what the study has already looked at decides how much the numbers can confirm.
 */
export const describeRunExposure = (
    exposure: IdeaLabRunExposure,
    run: { endDateTime: string; walkForwardStartDateTime: string | null }
): { body: string; detail: string } => {
    const tried = `This study has tried ${countOf(exposure.studyVariantCount, 'variant')} in ${countOf(exposure.studyRunCount, 'completed run')}.`;
    const walkForward =
        exposure.walkForward === null || run.walkForwardStartDateTime === null
            ? 'This run has no walk-forward window, so every period it shows has already informed the study.'
            : `Its walk-forward period (${run.walkForwardStartDateTime} to ${run.endDateTime}) has been shown by ${countOf(exposure.walkForward.runCount, 'run')} covering ${countOf(exposure.walkForward.variantCount, 'variant')}, so it has informed choices and is not an untouched confirmation. A period nobody has looked at yet, such as a later window, is the honest next test.`;

    return {
        body: `${tried} ${walkForward}`,
        detail: [
            countOf(exposure.windowCount, 'window'),
            countOf(exposure.universeCount, 'universe'),
            countOf(exposure.horizonSetCount, 'horizon set'),
            countOf(exposure.optionPolicyCount, 'option policy', 'option policies')
        ].join(' · ')
    };
};

export const describePromotionExposure = (exposure: IdeaLabPromotionExposure): string => {
    if (exposure === null) {
        return 'Not recorded: this promotion predates exposure tracking.';
    }

    const chosen = `Chosen after ${countOf(exposure.studyVariantCount, 'variant')} in ${countOf(exposure.studyRunCount, 'run')}`;

    return exposure.walkForward === null
        ? `${chosen}; an exploratory selection, not an untouched confirmation.`
        : `${chosen}; the walk-forward period had been shown by ${countOf(exposure.walkForward.runCount, 'run')} covering ${countOf(exposure.walkForward.variantCount, 'variant')}. An exploratory selection, not an untouched confirmation.`;
};

/** Exact native money is transported as integer text; formatting never drives allocation. */
export const formatNativeRawMoney = (raw: string | null): string => {
    if (raw === null) return '—';
    const value = BigInt(raw);
    const absolute = value < 0n ? -value : value;
    const fraction = (absolute % 1_000_000_000n).toString().padStart(9, '0').replace(/0+$/, '');
    return `${value < 0n ? '-' : ''}$${absolute / 1_000_000_000n}${fraction ? `.${fraction}` : ''}`;
};
